import React, { useEffect, useState } from 'react';
import ClayForm, { ClayInput, ClayRadio, ClayRadioGroup } from '@clayui/form';
import ClayDatePicker from '@clayui/date-picker';
import PhoneInputWithCountry from 'react-phone-number-input/react-hook-form';
import { isValidPhoneNumber } from 'react-phone-number-input';
import {
    UseFormRegister,
    FieldValues,
    FieldErrors,
    Control,
    Controller,
    UseFormWatch,
    UseFormSetError,
    UseFormClearErrors
} from 'react-hook-form';
import './FormComponent.css';
import CustomMultiSelect from '../customMultiSelect/CustomMultiSelect';
import CustomSelect from '../customSelect/CustomSelect';
import DatePickerComponent from '../DatePicker/DatePickerMUI';
import ClayLayout from '@clayui/layout';
import { renderLabelWithAsterisk } from '../../utils/commanUtil';
// import InfoTooltip from '../infoTooltip/InfoToolTip';

interface FormComponentProps {
    control: Control<FieldValues, any>;
    label?: string;
    type: string;
    name: string;
    placeholder: string;
    register: UseFormRegister<FieldValues>;
    errors: FieldErrors<FieldValues>;
    required?: boolean;
    disabled?: boolean;
    defaultValue?: any;
    watch: UseFormWatch<FieldValues>;
    setError: UseFormSetError<FieldValues>;
    className?: string;
    size?: number;
    errorTitle?: string;
    readOnly?: boolean;
    clearErrors?: UseFormClearErrors<FieldValues>;
    setIsEditButtonDisabled?: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedFiles?: React.Dispatch<React.SetStateAction<File[]>>;
    selectedFiles?: any;
    component?:
        | 'input'
        | 'textarea'
        | React.ForwardRefExoticComponent<any>
        | undefined;
    options?: {
        key: string;
        name: string;
        organization: string;
    }[]; // Keeping the original options type
    desc?: string;
    setDeleteDocId?: any;
    isPartnerType?: boolean;
    customErrorMsg?: string;
}

const FormComponent: React.FC<FormComponentProps> = ({
    label: formLabel,
    type,
    name,
    placeholder,
    register,
    errors,
    control,
    required,
    disabled,
    className,
    defaultValue,
    readOnly,
    options,
    errorTitle,
    component,
    watch,
    setSelectedFiles,
    selectedFiles,
    clearErrors,
    setDeleteDocId,
    desc,
    isPartnerType,
    customErrorMsg
}) => {
    const label = renderLabelWithAsterisk(formLabel);

    const [error, setError] = useState<string>('');
    const supportedFileTypes = [
        'image/jpeg',
        'image/png',
        'image/jpg',
        'application/pdf',
        'text/html'
    ];
    const supportedExcelFileExtensions = [
        '.xlsx',
        '.xls',
        '.csv',
        'application/pdf'
    ];

    const supportedExcelMimeTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
        'text/csv' // .csv
    ];
    const partnersPrimaryEmailValue = watch('partnersPrimaryEmail');
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event?.target?.files;
        if (files && files.length > 0) {
            if (files.length > 1) {
                setError('You can only upload one file at a time.');
                return false;
            }
            const file = files[0];
            let errorMessage = '';
            if (!supportedFileTypes.includes(file.type)) {
                errorMessage =
                    'Selected file type is not supported. Please select JPEG, JPG, PDF, or PNG files.';
            } else if (file.size > MAX_FILE_SIZE) {
                errorMessage = `Max allowed size is ${
                    MAX_FILE_SIZE / (1024 * 1024)
                } MB. File "${file.name}" exceeds the limit.`;
            }

            if (errorMessage) {
                setError(errorMessage);
                return false;
            } else {
                setError('');
                if (setSelectedFiles) {
                    setSelectedFiles([file]); // Only one file is stored, replacing any previous selections
                }
                return true;
            }
        }
    };

    const handleMultipleFileSelect = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const files = event?.target?.files;

        if (files && files.length > 0) {
            const selectedFilesArray = Array.from(files);
            let errorMessage = '';
            for (const file of selectedFilesArray) {
                const fileExtension = `.${
                    file.name.split('.').pop()?.toLowerCase() || ''
                }`;

                if (
                    name !== 'uploadDelegationFile' &&
                    !supportedFileTypes.includes(file.type)
                ) {
                    errorMessage =
                        'Selected file type is not supported. Please select JPEG, JPG, PDF, or PNG files.';
                    break;
                }

                if (
                    name === 'uploadDelegationFile' &&
                    !supportedExcelMimeTypes.includes(file.type) &&
                    !supportedExcelFileExtensions.includes(fileExtension)
                ) {
                    errorMessage =
                        'Selected file type is not supported. Please select PDF, XLSX, XLS, or CSV files.';
                    break;
                }

                if (file.size > MAX_FILE_SIZE) {
                    errorMessage = `Max allowed size is ${
                        MAX_FILE_SIZE / (1024 * 1024)
                    } MB. File "${file.name}" exceeds the limit.`;
                    break;
                }
            }

            if (errorMessage) {
                setError(errorMessage);
                return false;
            } else {
                setError('');
                if (setSelectedFiles) {
                    const uploadedFile = [
                        ...selectedFiles,
                        ...selectedFilesArray
                    ];
                    setSelectedFiles(uploadedFile);
                    // setSelectedFiles(selectedFilesArray); // Store all selected files
                }
                return true;
            }
        }
    };

    const handleRemoveFile = (fileName: string) => {
        setDeleteDocId && setDeleteDocId(selectedFiles[0].objectEntryId);
        setSelectedFiles &&
            setSelectedFiles((prevFiles) => {
                const updatedFiles = prevFiles.filter(
                    (file) => file.name !== fileName
                );

                return updatedFiles;
            });
        setError('');
    };

    const getClassName = () => {
        let baseClass = 'custom_input book-input no-spinner';
        // Add the 'is-invalid' class if there is an error
        if (errors[name]) {
            baseClass += ' is-invalid';
        }
        // Conditionally apply the class with important styles
        if (name === 'background' || name === 'objective') {
            baseClass += ' textarea-special';
        }
        return baseClass;
    };

    return (
        <div className={className}>
            {type === 'text' && (
                <ClayForm.Group className={className}>
                    {label && (
                        <label
                            className={
                                name === 'background' || name === 'objective'
                                    ? 'bold-heading'
                                    : ''
                            }
                        >
                            {(() => {
                                if (desc) {
                                    return (
                                        <>
                                            {label}{' '}
                                            <i
                                                className="i-font"
                                                style={{ fontSize: '14px' }}
                                            >
                                                {desc}
                                            </i>
                                        </>
                                    );
                                } else if (
                                    isPartnerType ||
                                    name == 'serviceOfferType'
                                ) {
                                    return <span>{label}</span>;
                                } else {
                                    return label;
                                }
                            })()}
                        </label>
                    )}

                    <ClayInput
                        id={name}
                        type="text"
                        component={component}
                        placeholder={placeholder}
                        {...(readOnly
                            ? { value: defaultValue, readOnly: true }
                            : {
                                  ...register(name, {
                                      required:
                                          !disabled &&
                                          required &&
                                          `${errorTitle} is required`,
                                      ...(component !== 'textarea' && {
                                          maxLength: {
                                              value: 280,
                                              message:
                                                  'Maximum 280 characters allowed'
                                          }
                                      }),
                                      validate: (value) => {
                                          // Run checks only if field is required
                                          if (required) {
                                              if (!value?.trim()) {
                                                  return 'Only spaces are not allowed';
                                              }

                                              // 🔹 Email validation
                                              if (
                                                  [
                                                      'requestorWorkEmail',
                                                      'email'
                                                  ].includes(name) &&
                                                  !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                                                      value
                                                  )
                                              ) {
                                                  return 'Invalid email address';
                                              }

                                              // 🔹 Company website validation
                                              if (
                                                  name ===
                                                      'requestorCompanyWebsiteURL' &&
                                                  !/^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/.test(
                                                      value
                                                  )
                                              ) {
                                                  return 'Invalid URL';
                                              }

                                              // 🔹 Description validation (max 40 words)
                                              if (name === 'description') {
                                                  const wordCount = value
                                                      .trim()
                                                      .split(/\s+/).length;
                                                  if (wordCount > 40) {
                                                      return 'Description cannot exceed 40 words';
                                                  }
                                              }

                                              if (
                                                  name ==
                                                  'requestorBusinessIncorporationYear'
                                              ) {
                                                  const currentYear =
                                                      new Date().getFullYear();
                                                  const year = Number(value);

                                                  if (isNaN(year)) {
                                                      return 'Year must be a valid number';
                                                  }

                                                  if (year > currentYear) {
                                                      return `Year cannot be in the future year.`;
                                                  }
                                              }
                                              return true;
                                          } else {
                                              // Optional fields → allow empty, but reject pure spaces
                                              if (value && !value?.trim()) {
                                                  return 'Only spaces are not allowed';
                                              }
                                          }

                                          return true;
                                      }
                                  })
                              })}
                        defaultValue={defaultValue}
                        autoComplete="off"
                        disabled={disabled}
                        readOnly={readOnly}
                        className={getClassName()}
                    />
                    {errors[name] && (
                        <div
                            className="invalid-feedback"
                            style={{ display: 'block' }}
                        >
                            {errors[name]?.message as string}
                        </div>
                    )}
                    {customErrorMsg && (
                        <div
                            className="invalid-feedback"
                            style={{ display: 'block' }}
                        >
                            {customErrorMsg}
                        </div>
                    )}
                </ClayForm.Group>
            )}
            {type === 'select' && (
                <CustomSelect
                    control={control}
                    label={label}
                    name={name}
                    placeholder={placeholder}
                    required={required}
                    disabled={disabled}
                    defaultValue={defaultValue}
                    options={options}
                    errors={errors}
                    errorTitle={errorTitle}
                    isPartnerType={isPartnerType}
                />
            )}
            {type === 'multiSelect' && (
                <ClayForm.Group
                    className={name === 'view-calendar' ? 'm-0' : ''}
                >
                    {label && (
                        <label htmlFor={name}>
                            {(() => {
                                if (isPartnerType) {
                                    return <span>{label}</span>;
                                } else {
                                    return label;
                                }
                            })()}
                        </label>
                    )}
                    <Controller
                        control={control}
                        name={name}
                        defaultValue={defaultValue}
                        rules={{
                            required: required && `${errorTitle} is required`
                        }}
                        render={({ field: { onChange, value } }) => {
                            const formattedOptions =
                                options?.map((option) =>
                                    name === 'test'
                                        ? {
                                              key: option.organization,
                                              name: option.organization
                                          }
                                        : { key: option.key, name: option.name }
                                ) || [];
                            return (
                                <CustomMultiSelect
                                    value={value || []}
                                    handleOnChange={onChange}
                                    register={register}
                                    required={false}
                                    errors={errors}
                                    options={formattedOptions}
                                    name={name}
                                    placeholder={placeholder}
                                    disabled={disabled}
                                />
                            );
                        }}
                    />
                </ClayForm.Group>
            )}
            {type === 'number' && (
                <ClayForm.Group className={className}>
                    {label && (
                        <label htmlFor={name}>
                            {(() => {
                                if (isPartnerType) {
                                    return <span>{label}</span>;
                                } else {
                                    return label;
                                }
                            })()}
                        </label>
                    )}
                    <ClayInput
                        id={name}
                        type="number"
                        min={1}
                        onWheel={(e) => e.currentTarget.blur()} // Prevent scrolling
                        onKeyDown={(e) => {
                            if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        placeholder={placeholder}
                        {...(readOnly
                            ? { value: defaultValue, readOnly: true }
                            : {
                                  ...register(name, {
                                      required:
                                          required &&
                                          `${errorTitle} is required`,
                                      validate: {
                                          isPositive: (value: number) =>
                                              value > 0 ||
                                              `${errorTitle} value must be 0 or greater`,
                                          isNumber: (value: string) =>
                                              /^\d+$/.test(value) ||
                                              'Only digits are allowed'
                                      }
                                  })
                              })}
                        // defaultValue={defaultValue}
                        autoComplete="off"
                        disabled={disabled}
                        className={
                            errors[name]
                                ? 'is-invalid'
                                : ` no-spinner custom_input book-input no-spinner`
                        }
                    />
                    {errors[name] && (
                        <div
                            className="invalid-feedback"
                            style={{ display: 'block' }}
                        >
                            {errors[name]?.message as string}
                        </div>
                    )}
                </ClayForm.Group>
            )}
            {type === 'date' && clearErrors && (
                <ClayForm.Group>
                    {label && (
                        <label htmlFor={name}>
                            {(() => {
                                if (isPartnerType) {
                                    return <span>{label}</span>;
                                } else {
                                    return label;
                                }
                            })()}
                        </label>
                    )}
                    <DatePickerComponent
                        watch={watch}
                        name={name}
                        label={errorTitle ?? ''}
                        control={control}
                        errors={errors}
                        placeholderText={placeholder ?? ''}
                        clearErrors={clearErrors}
                        disabled={disabled}
                    />
                </ClayForm.Group>
            )}
            {type === 'phone' && (
                <ClayForm.Group className={className}>
                    {label && (
                        <label htmlFor={name} style={{ fontWeight: 500 }}>
                            {(() => {
                                if (isPartnerType) {
                                    return <span>{label}</span>;
                                } else {
                                    return label;
                                }
                            })()}
                        </label>
                    )}
                    <Controller
                        name={name}
                        control={control}
                        rules={{
                            validate: (value: string) => {
                                if (!value) return `${errorTitle} is required`;
                                if (!isValidPhoneNumber(value))
                                    return `${errorTitle} is not valid`;

                                return true;
                            }
                        }}
                        defaultValue={defaultValue} // Set default value
                        render={({ field }) => (
                            <PhoneInputWithCountry
                                name={name}
                                international={true}
                                control={control as any}
                                rules={{
                                    validate: (value: string) => {
                                        if (!value)
                                            return `${errorTitle} is required`;
                                        if (!isValidPhoneNumber(value))
                                            return `${errorTitle} is not valid`;

                                        return true;
                                    }
                                }}
                                className={errors[name] ? 'is-invalid' : ''}
                                numberInputProps={{
                                    className: 'form-control'
                                }}
                            />
                        )}
                    />
                    {errors[name] && (
                        <div
                            className="invalid-feedback"
                            style={{ display: 'block' }}
                        >
                            {errors[name]?.message as string}
                        </div>
                    )}
                </ClayForm.Group>
            )}
            {type === 'file' && (
                <ClayForm.Group className={className}>
                    {label && (
                        <label htmlFor={name} style={{ fontWeight: 500 }}>
                            {(() => {
                                if (isPartnerType) {
                                    return <span>{label}</span>;
                                } else {
                                    return label;
                                }
                            })()}
                        </label>
                    )}
                    <Controller
                        control={control}
                        name={name}
                        defaultValue={defaultValue}
                        rules={{
                            required:
                                required &&
                                selectedFiles?.length === 0 &&
                                `${errorTitle} is required`
                        }}
                        render={({ field: { onChange, value } }) => {
                            return (
                                <>
                                    <div className="infocus-input-file-wrapper">
                                        <i className="input-attach-icon">
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M8.462 17.1732C6.94733 17.1732 5.65833 16.6516 4.595 15.6082C3.53167 14.5642 3 13.2876 3 11.7782C3 10.2702 3.53167 8.9909 4.595 7.94024C5.65833 6.88957 6.94733 6.36457 8.462 6.36524H17.192C18.25 6.36524 19.149 6.72924 19.889 7.45724C20.629 8.18524 20.9993 9.0779 21 10.1352C21.0007 11.1926 20.6307 12.0852 19.89 12.8132C19.1493 13.5412 18.25 13.9046 17.192 13.9032H8.923C8.33633 13.9032 7.83067 13.6986 7.406 13.2892C6.98133 12.8799 6.769 12.3806 6.769 11.7912C6.769 11.2019 6.978 10.6952 7.396 10.2712C7.814 9.84724 8.323 9.63524 8.923 9.63524H16.731C16.8723 9.63524 16.991 9.68324 17.087 9.77924C17.183 9.87524 17.231 9.9939 17.231 10.1352C17.231 10.2766 17.183 10.3952 17.087 10.4912C16.991 10.5872 16.8723 10.6352 16.731 10.6352H8.923C8.60367 10.6352 8.33167 10.7446 8.107 10.9632C7.88233 11.1819 7.76967 11.4506 7.769 11.7692C7.76833 12.0879 7.881 12.3569 8.107 12.5762C8.333 12.7956 8.605 12.9049 8.923 12.9042H17.212C17.9887 12.9002 18.6473 12.6332 19.188 12.1032C19.7293 11.5726 20 10.9166 20 10.1352C20 9.3599 19.7263 8.70424 19.179 8.16824C18.6317 7.6329 17.9693 7.36524 17.192 7.36524H8.462C7.22733 7.3619 6.175 7.78957 5.305 8.64824C4.435 9.50757 4 10.5536 4 11.7862C4 13.0016 4.435 14.0349 5.305 14.8862C6.175 15.7376 7.22733 16.1666 8.462 16.1732H16.731C16.8723 16.1732 16.991 16.2212 17.087 16.3172C17.183 16.4132 17.231 16.5322 17.231 16.6742C17.231 16.8162 17.183 16.9349 17.087 17.0302C16.991 17.1256 16.8723 17.1732 16.731 17.1732H8.462Z"
                                                    fill="#003764"
                                                />
                                            </svg>
                                        </i>
                                        <input
                                            id="file-input"
                                            type="file"
                                            onChange={(e) => {
                                                onChange(e.target.value);
                                                if (
                                                    name === 'uploadMOU' ||
                                                    name === 'uploadSLA' ||
                                                    name ==
                                                        'corporatePresentation'
                                                ) {
                                                    handleMultipleFileSelect(e);
                                                } else {
                                                    handleFileSelect(e);
                                                }
                                            }}
                                            className="input-file"
                                            disabled={disabled}
                                        />
                                        <label
                                            htmlFor="file-input"
                                            className="input-file-label"
                                        >
                                            Browse the document
                                        </label>
                                    </div>
                                    {errors[name] && (
                                        <div
                                            className="invalid-feedback"
                                            style={{ display: 'block' }}
                                        >
                                            {errors[name]?.message as string}
                                        </div>
                                    )}
                                    {error && (
                                        <div
                                            className="invalid-feedback"
                                            style={{ display: 'block' }}
                                        >
                                            {error}
                                        </div>
                                    )}
                                    {selectedFiles?.length > 0 && (
                                        <div className="mt-0 selected-input-files">
                                            {selectedFiles.map((file: File) => (
                                                <div
                                                    key={file.name}
                                                    className="input-file-badge"
                                                >
                                                    {file.name}
                                                    <button
                                                        className="remove-input-file-btn"
                                                        onClick={() => {
                                                            handleRemoveFile(
                                                                file.name
                                                            );
                                                            onChange(
                                                                selectedFiles.filter(
                                                                    (selectedFile: {
                                                                        name: string;
                                                                    }) =>
                                                                        selectedFile.name !==
                                                                        file.name
                                                                )
                                                            );
                                                        }}
                                                    >
                                                        &times;
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            );
                        }}
                    />
                </ClayForm.Group>
            )}
        </div>
    );
};

export default FormComponent;
